import React, { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTHS = ['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'];
const WEEKDAYS = ['Mo','Di','Mi','Do','Fr','Sa','So'];

const pad = (n) => String(n).padStart(2, '0');
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const BookingSection = () => {
  const now = new Date();
  const today = startOfDay(now);
  // Same-day cutoff: after 13:00 the current day can no longer be booked
  const isTodayLocked = now.getHours() >= 13;
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth()); // 0-11
  const [fullDays, setFullDays] = useState(new Set());
  const [selectedDate, setSelectedDate] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', service: '', message: '' });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/bookings/availability`, { params: { year: viewYear, month: viewMonth + 1 } })
      .then(({ data }) => { if (alive) setFullDays(new Set(data.full_days || [])); })
      .catch(() => {});
    return () => { alive = false; };
  }, [viewYear, viewMonth]);

  const shift = (delta) => {
    let m = viewMonth + delta, y = viewYear;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    setViewYear(y); setViewMonth(m); setSelectedDate(null);
  };

  const firstDay = new Date(viewYear, viewMonth, 1);
  const firstWeekdayIdx = (firstDay.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstWeekdayIdx; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(viewYear, viewMonth, d));

  const isPast = (d) => {
    if (d < today) return true;
    // Today is locked after 13:00
    if (toISO(d) === toISO(today) && isTodayLocked) return true;
    return false;
  };
  const isFull = (d) => fullDays.has(toISO(d));
  const isSelected = (d) => selectedDate && toISO(d) === toISO(selectedDate);

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedDate) { toast.error('Bitte wähle einen Termin.'); return; }
    if (!form.name || !form.email) { toast.error('Bitte fülle alle Pflichtfelder aus.'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/bookings`, { ...form, date: toISO(selectedDate) });
      setDone(true);
      toast.success('Termin ist gebucht!');
      // Reload availability
      const { data } = await axios.get(`${API}/bookings/availability`, { params: { year: viewYear, month: viewMonth + 1 } });
      setFullDays(new Set(data.full_days || []));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Buchung fehlgeschlagen.');
    } finally { setSending(false); }
  };

  const resetForm = () => {
    setForm({ name: '', email: '', phone: '', service: '', message: '' });
    setSelectedDate(null);
    setDone(false);
  };

  return (
    <section id="booking" className="bg-charcoal py-20 md:py-28">
      <div className="max-w-6xl mx-auto px-6">
        <h2 className="section-title text-center mb-4">Erstgespräch vereinbaren</h2>
        <p className="text-zinc-300 text-lg text-center max-w-2xl mx-auto mb-12">
          Wähle einen passenden Tag im Kalender – wir melden uns bei dir zur Absprache der genauen Uhrzeit.
        </p>

        {done ? (
          <div className="max-w-md mx-auto bg-[#252525] rounded-lg p-8 border border-zinc-800 text-center">
            <div className="w-16 h-16 rounded-full bg-yellow flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-9 h-9 text-[#1a1a1a]" />
            </div>
            <h3 className="font-display text-yellow text-2xl mb-3">Termin gebucht!</h3>
            <p className="text-zinc-300 mb-6">Wir melden uns zeitnah bei dir zur Absprache der Uhrzeit.</p>
            <button onClick={resetForm} className="btn-dark">Weiteren Termin buchen</button>
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-[#252525] rounded-lg p-6 border border-zinc-800">
              <div className="flex items-center justify-between mb-4">
                <button onClick={() => shift(-1)} className="p-2 rounded hover:bg-zinc-800 text-yellow">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <h3 className="font-heavy text-white text-lg">{MONTHS[viewMonth]} {viewYear}</h3>
                <button onClick={() => shift(1)} className="p-2 rounded hover:bg-zinc-800 text-yellow">
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
              <div className="grid grid-cols-7 gap-1 mb-2">
                {WEEKDAYS.map((w) => (
                  <div key={w} className="text-center text-yellow text-xs font-heavy py-2">{w}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {cells.map((d, idx) => {
                  if (!d) return <div key={idx} />;
                  const past = isPast(d);
                  const full = isFull(d);
                  const disabled = past || full;
                  const selected = isSelected(d);
                  return (
                    <button
                      key={idx}
                      type="button"
                      disabled={disabled}
                      onClick={() => setSelectedDate(d)}
                      className={`aspect-square rounded-md text-sm font-medium transition-colors
                        ${selected ? 'bg-yellow text-[#1a1a1a]' :
                          disabled ? 'bg-transparent text-zinc-700 cursor-not-allowed line-through' :
                          'bg-[#1c1c1c] text-white hover:bg-yellow/20'}`}
                    >
                      {d.getDate()}
                    </button>
                  );
                })}
              </div>
              <p className="text-zinc-500 text-xs mt-4">Ausgegraute Tage sind vergangen oder ausgebucht.</p>
            </div>

            <form onSubmit={submit} className="bg-[#252525] rounded-lg p-6 border border-zinc-800">
              <div className="flex items-center gap-3 mb-5 pb-4 border-b border-zinc-800">
                <CalendarIcon className="w-5 h-5 text-yellow" />
                <span className="text-white font-heavy">
                  {selectedDate ? selectedDate.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : 'Bitte Tag wählen'}
                </span>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-yellow text-sm font-semibold" htmlFor="b-name">Name *</label>
                  <input id="b-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                    className="mt-1 w-full bg-transparent border-b-2 border-zinc-600 focus:border-yellow outline-none py-2 text-white" />
                </div>
                <div>
                  <label className="text-yellow text-sm font-semibold" htmlFor="b-email">Email *</label>
                  <input id="b-email" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
                    className="mt-1 w-full bg-transparent border-b-2 border-zinc-600 focus:border-yellow outline-none py-2 text-white" />
                </div>
                <div>
                  <label className="text-yellow text-sm font-semibold" htmlFor="b-phone">Telefon <span className="text-zinc-500 font-normal">(optional)</span></label>
                  <input id="b-phone" type="tel" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})}
                    className="mt-1 w-full bg-transparent border-b-2 border-zinc-600 focus:border-yellow outline-none py-2 text-white" />
                </div>
                <div>
                  <label className="text-yellow text-sm font-semibold" htmlFor="b-service">Thema / Service <span className="text-zinc-500 font-normal">(optional)</span></label>
                  <input id="b-service" value={form.service} onChange={(e) => setForm({...form, service: e.target.value})}
                    placeholder="z. B. Videoproduktion"
                    className="mt-1 w-full bg-transparent border-b-2 border-zinc-600 focus:border-yellow outline-none py-2 text-white placeholder:text-zinc-500" />
                </div>
                <div>
                  <label className="text-yellow text-sm font-semibold" htmlFor="b-msg">Nachricht <span className="text-zinc-500 font-normal">(optional)</span></label>
                  <textarea id="b-msg" rows={3} value={form.message} onChange={(e) => setForm({...form, message: e.target.value})}
                    className="mt-1 w-full bg-transparent border-b-2 border-zinc-600 focus:border-yellow outline-none py-2 text-white resize-none" />
                </div>
              </div>
              <button type="submit" disabled={sending || !selectedDate} className="btn-yellow mt-6 disabled:opacity-50">
                {sending ? 'Buchen...' : 'Termin buchen'}
              </button>
            </form>
          </div>
        )}
      </div>
    </section>
  );
};

export default BookingSection;
